// SAB-432 (e): /state-availability — the dedicated eligibility matrix.
//
// Reads from check-eligibility get_states (DB-backed, same source TrustLegality
// uses). Renders status badge (eligible / restricted / license_required),
// minimum age, and legal notes for all 51 jurisdictions.
//
// Footer + TrustLegality link here in 4 places (was 404 until this commit).
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import MarketingLayout from '@/components/shared/MarketingLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { deriveAgeLine } from '@/lib/eligibility/derive';

type StateStatus = 'eligible' | 'restricted' | 'license_required';

interface StateRow {
  state_code: string;
  state_name: string;
  fantasy_legal: boolean;
  minimum_age: number;
  status: StateStatus;
  notes: string | null;
}

const STATUS_LABEL: Record<StateStatus, string> = {
  eligible: 'Eligible',
  restricted: 'Restricted',
  license_required: 'License required',
};

const STATUS_CLASS: Record<StateStatus, string> = {
  eligible: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  restricted: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
  license_required: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
};

export default function StateAvailability() {
  const [states, setStates] = useState<StateRow[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: invokeErr } = await supabase.functions.invoke(
          'check-eligibility',
          { body: { action: 'get_states' } },
        );
        if (invokeErr) throw invokeErr;
        if (cancelled) return;
        const rows = (data as { states?: StateRow[] } | null)?.states ?? [];
        if (rows.length === 0) throw new Error('get_states returned empty');
        setStates(rows);
      } catch (err) {
        if (cancelled) return;
        Sentry.addBreadcrumb({
          category: 'marketing.state-availability',
          level: 'warning',
          message: 'get_states failed — rendering neutral fallback',
          data: { error: String(err) },
        });
        Sentry.captureException(err, { tags: { component: 'StateAvailability' } });
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const ageLine = states
    ? deriveAgeLine(
        states.map((s) => ({
          code: s.state_code,
          minAge: s.minimum_age,
          fantasyLegal: s.fantasy_legal,
        })),
        'full',
      )
    : '';

  const eligibleCount = states ? states.filter((s) => s.fantasy_legal).length : 0;

  return (
    <MarketingLayout>
      <header className="space-y-3">
        <h1 className="text-2xl font-display font-bold text-foreground m-0">
          Where you can play
        </h1>
        <p className="text-sm text-muted-foreground m-0 leading-relaxed">
          {error ? (
            <>
              Eligibility data is temporarily unavailable. Please contact{' '}
              <a
                href="mailto:support@sabotagefantasy.com"
                className="underline text-foreground hover:text-primary"
              >
                support
              </a>{' '}
              if this persists.
            </>
          ) : states ? (
            <>
              Sabotage Fantasy is available in {eligibleCount} of {states.length} US
              jurisdictions. {ageLine} We operate as a season-long fantasy sports
              platform under the federal UIGEA safe harbor.
            </>
          ) : (
            'Loading current eligibility…'
          )}
        </p>
      </header>

      <div className="flex flex-wrap gap-2 text-xs">
        {(Object.keys(STATUS_LABEL) as StateStatus[]).map((s) => (
          <span
            key={s}
            className={`inline-flex items-center px-2 py-0.5 rounded-md border font-bold ${STATUS_CLASS[s]}`}
          >
            {STATUS_LABEL[s]}
          </span>
        ))}
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-0">
          {error ? (
            <div className="p-6 text-sm text-muted-foreground">
              No state data available right now.
            </div>
          ) : states === null ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/20 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-4 py-2 font-bold w-[34%]">State</th>
                  <th className="text-left px-4 py-2 font-bold w-[22%]">Status</th>
                  <th className="text-left px-4 py-2 font-bold w-[10%]">Min age</th>
                  <th className="text-left px-4 py-2 font-bold">Notes</th>
                </tr>
              </thead>
              <tbody>
                {states.map((s) => (
                  <tr
                    key={s.state_code}
                    className="border-b border-border/30 last:border-b-0 align-top"
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="font-display font-bold text-foreground">
                        {s.state_code}
                      </span>{' '}
                      <span className="text-muted-foreground">{s.state_name}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge
                        variant="outline"
                        className={`text-[10px] font-bold ${STATUS_CLASS[s.status] ?? ''}`}
                      >
                        {STATUS_LABEL[s.status] ?? s.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-foreground">
                      {s.minimum_age}+
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs leading-relaxed">
                      {s.notes ?? '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Eligibility changes as state law evolves; check back if you've moved. If
        you believe a listing is incorrect, contact{' '}
        <a
          href="mailto:support@sabotagefantasy.com"
          className="underline text-foreground hover:text-primary"
        >
          support@sabotagefantasy.com
        </a>
        .
      </p>
    </MarketingLayout>
  );
}
